import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Card,
  Row,
  Col,
  Label,
  Input,
  TabContent,
  TabPane,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useBoolean } from 'usehooks-ts';

import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import Pagination from 'components/Pagination';
import DataTableBooking from 'components/App/DataTableBooking';
import Button from 'components/App/Button';

import { BOOKING_TAB, themeColor } from 'constants/index';
import { getParamsFormObject } from 'utils';
import DownloadIcon from 'assets/images/customer/download.svg';
import DropDownIcon from 'assets/images/chervon-white.svg';
import cn from 'utils/classnames';

import styles from './LocationManagerBooking.module.scss';

function urlParamToDayjs(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  let d = dayjs(trimmed);
  if (d.isValid()) return d;
  const parts = trimmed.split('/');
  if (parts.length === 3) {
    const [dd, mm, yyyy] = parts.map((p) => p.trim());
    if (!dd || !mm || !yyyy) return null;
    d = dayjs(`${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`);
    if (d.isValid()) return d;
  }
  return null;
}

const breadcrumbList = [
  {
    name: 'Danh sách đặt phòng',
    isActive: true,
    link: '', // TODO: ADD ROUTE WHEN HAVE LINK
  },
];

const NAV_TAB = [
  {
    title: 'Tất cả',
    value: BOOKING_TAB.ALL,
  },
  {
    title: 'Chờ thanh toán',
    value: BOOKING_TAB.PENDING,
  },
  {
    title: 'Đã đặt phòng',
    value: BOOKING_TAB.BOOKED,
  },
  // {
  //   title: 'Đã xác nhận',
  //   value: BOOKING_TAB.APPROVED,
  // },
  {
    title: 'Đã hoàn thành',
    value: BOOKING_TAB.FINISHED,
  },
  {
    title: 'Đã hủy',
    value: BOOKING_TAB.CANCELLED,
  },
  {
    title: 'Đã thay đổi thông tin đặt phòng',
    value: BOOKING_TAB.MODIFIED,
  },
];

const FILTER_BY_DATE = [
  {
    label: 'Ngày đặt phòng',
    value: 'BOOKING',
  },
  {
    label: 'Ngày nhận phòng',
    value: 'CHECK_IN',
  },
  {
    label: 'Ngày trả phòng',
    value: 'CHECK_OUT',
  },
];

const DEFAULT_LIMIT = 20;

function LocationManagerBooking(props) {
  const {
    loadingBookingList,
    isLoadingExportBookingList,

    bookingList,
    bookingTotal,

    actionGetBookingList,
    actionExportBookingList,
    actionGetMyPlace,
  } = props;

  const tableRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const {
    value: isOpenDropdownFilterByDate,
    toggle: toggleDropdownFilterByDate,
  } = useBoolean(false);

  const [filterDraft, setFilterDraft] = useState({
    dateFilterType: '',
    startDate: '',
    endDate: '',
  });
  const [searchDraft, setSearchDraft] = useState('');

  const history = useHistory();

  const params = new URLSearchParams(history.location.search);

  const searchQueries = useMemo(
    () => ({
      page: params.get('page') || 1,
      q: params.get('q') || '',
      limit: DEFAULT_LIMIT,
      dateFilterType: params.get('dateFilterType') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
      statusType: params.get('statusType') || '',
      place: params.get('place') || '',
    }),
    [history.location.search],
  );

  const onChangePage = useCallback(
    (selected) => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          page: selected.selected + 1,
          limit: DEFAULT_LIMIT,
        }),
      });
      tableRef.current.scrollIntoView({ behavior: 'smooth' });
    },
    [history, searchQueries],
  );

  const onSearchInputChange = useCallback((e) => {
    setSearchDraft(e.target.value);
  }, []);

  const totalPage = useMemo(
    () => Math.ceil((bookingTotal || 0) / DEFAULT_LIMIT),
    [bookingTotal],
  );

  const disabledStartDate = useCallback(
    (current) => {
      if (!current) return false;
      const end = urlParamToDayjs(filterDraft.endDate);
      if (!end) return false;
      return current.startOf('day').valueOf() > end.startOf('day').valueOf();
    },
    [filterDraft.endDate],
  );

  const disabledEndDate = useCallback(
    (current) => {
      if (!current) return false;
      const start = urlParamToDayjs(filterDraft.startDate);
      if (!start) return false;
      return current.startOf('day').valueOf() < start.startOf('day').valueOf();
    },
    [filterDraft.startDate],
  );

  const onSelectFilterByDate = useCallback((type) => () => {
    setFilterDraft((prev) => ({ ...prev, dateFilterType: type }));
  }, []);

  const onSelectDate = useCallback(
    (keyValue) => (_, dateString) => {
      setFilterDraft((prev) => ({
        ...prev,
        [keyValue]: dateString || '',
      }));
    },
    [],
  );

  const canSubmitSearch = useMemo(
    () =>
      !!(
        searchDraft.trim()
        || (
          filterDraft.dateFilterType
          && filterDraft.startDate
          && filterDraft.endDate
        )
      ),
    [filterDraft, searchDraft],
  );

  const onSubmitSearch = useCallback(() => {
    if (!canSubmitSearch) return;
    const hasFullDate =
      filterDraft.dateFilterType
      && filterDraft.startDate
      && filterDraft.endDate;

    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        page: 1,
        q: searchDraft.trim(),
        ...(hasFullDate
          ? {
            dateFilterType: filterDraft.dateFilterType,
            startDate: filterDraft.startDate,
            endDate: filterDraft.endDate,
          }
          : {
            dateFilterType: '',
            startDate: '',
            endDate: '',
          }),
      }),
    });
  }, [canSubmitSearch, filterDraft, history, searchDraft, searchQueries]);

  const onResetFilters = useCallback(() => {
    setFilterDraft({
      dateFilterType: '',
      startDate: '',
      endDate: '',
    });
    setSearchDraft('');
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        dateFilterType: '',
        startDate: '',
        endDate: '',
        q: '',
        page: 1,
      }),
    });
  }, [history, searchQueries]);

  // Filter By Status
  const onSelectFilterByStatus = useCallback(
    (status) => () => {
      history.push({
        search: getParamsFormObject({
          ...searchQueries,
          statusType: status,
          page: 1,
        }),
      });
    },
    [history, searchQueries],
  );

  const onExportBookingList = useCallback(() => {
    const hasFullDate =
      searchQueries.dateFilterType
      && searchQueries.startDate
      && searchQueries.endDate;

    const payload = hasFullDate
      ? {
        q: searchQueries.q,
        dateFilterType: searchQueries.dateFilterType,
        startDate: searchQueries.startDate,
        endDate: searchQueries.endDate,
        statusType: searchQueries.statusType,
        place: searchQueries.place,
      }
      : {
        q: searchQueries.q,
        statusType: searchQueries.statusType,
        placeToFilter: searchQueries.place,
      };

    actionExportBookingList(payload);
  }, [actionExportBookingList, searchQueries]);

  useEffect(() => {
    actionGetMyPlace();
  }, []);

  useEffect(() => {
    setPageCount(totalPage);
  }, [totalPage]);

  useEffect(() => {
    setFilterDraft({
      dateFilterType: searchQueries.dateFilterType || '',
      startDate: searchQueries.startDate || '',
      endDate: searchQueries.endDate || '',
    });
  }, [
    searchQueries.dateFilterType,
    searchQueries.startDate,
    searchQueries.endDate,
  ]);

  useEffect(() => {
    setSearchDraft(searchQueries.q || '');
  }, [searchQueries.q]);

  useEffect(() => {
    if (
      searchQueries.dateFilterType &&
      searchQueries.startDate &&
      searchQueries.endDate
    ) {
      actionGetBookingList(searchQueries);
    } else {
      actionGetBookingList({
        page: searchQueries.page,
        q: searchQueries.q,
        limit: searchQueries.limit,
        statusType: searchQueries.statusType,
        placeToFilter: searchQueries.place,
      });
    }
  }, [searchQueries]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Danh sách đặt phòng" />

      <Container fluid>
        {/* Render Breadcrumb */}
        <Breadcrumb
          pageTitle="Danh sách đặt phòng"
          breadcrumbList={breadcrumbList}
        />

        <Loading loading={loadingBookingList} loaderOnPage />

        <Card className="Section">
          <Row>
            <Col xxl="6" className="mb-24">
              <Label className="Section__Search__Label">Tìm kiếm</Label>

              <div className="Section__Search__Container">
                <Input
                  placeholder="Tìm theo ID đơn đặt phòng hoặc Email khách"
                  className={cn('Section__Search__Input')}
                  value={searchDraft}
                  onChange={onSearchInputChange}
                />

                <i className="fas fa-search Section__Search__Icon__Search" />
              </div>
            </Col>

            <Col xxl="6">
              <Label className="Section__Search__Label">Bộ lọc</Label>

              <Row>
                <Col md="4" className="mb-24">
                  <Dropdown
                    isOpen={isOpenDropdownFilterByDate}
                    toggle={toggleDropdownFilterByDate}
                    className="Section__Search__Dropdown__Wrapper"
                  >
                    <DropdownToggle
                      className="Section__Search__Dropdown"
                      type="button"
                      tag="button"
                    >
                      {FILTER_BY_DATE.find(
                        (item) => item.value === filterDraft.dateFilterType,
                      )?.label || 'Theo ngày'}
                      <i className="mdi mdi-chevron-down" />
                    </DropdownToggle>

                    <DropdownMenu className="Section__Search__Dropdown__Menu">
                      {FILTER_BY_DATE.map((item) => (
                        <DropdownItem
                          key={item.value}
                          onClick={onSelectFilterByDate(item.value)}
                        >
                          {item.label}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </Col>

                <Col md="4" className="mb-24">
                  <DatePicker
                    className="Section__Search__DatePicker"
                    value={urlParamToDayjs(filterDraft.startDate)}
                    onChange={onSelectDate('startDate')}
                    placeholder="Từ ngày"
                    inputReadOnly
                    disabledDate={disabledStartDate}
                  />
                </Col>

                <Col md="4" className="mb-24">
                  <DatePicker
                    className="Section__Search__DatePicker"
                    value={urlParamToDayjs(filterDraft.endDate)}
                    onChange={onSelectDate('endDate')}
                    placeholder="Đến ngày"
                    inputReadOnly
                    disabledDate={disabledEndDate}
                  />
                </Col>
              </Row>
            </Col>

            <Col xxl="6">
              <div className="Section__Search__Button__Container">
                <Button
                  outline
                  classes="Section__Search__Button  Section__Search__Button__ReType"
                  color={themeColor.LIGHT}
                  onClick={onResetFilters}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button Section__Search__Button__Search"
                  type="button"
                  disabled={!canSubmitSearch}
                  onClick={onSubmitSearch}
                >
                  Tìm kiếm
                </Button>
              </div>
            </Col>
          </Row>
        </Card>

        <Card className="Section">
          <div className="Tab__Header__Container">
            <div className="Tab__Header" ref={tableRef}>
              <div className="Tab__Header__Scroll">
                {NAV_TAB.map((tab) => (
                  <div key={tab.value} className="Tab__Header__Tab">
                    <button
                      type="button"
                      onClick={onSelectFilterByStatus(tab.value)}
                      className={`Tab__Header__Tab__Item ${
                        (searchQueries.statusType === tab.value ||
                          (tab.value === BOOKING_TAB.ALL &&
                            !searchQueries.statusType)) &&
                        'Active'
                      }`}
                    >
                      {tab.title}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="button"
              classes="Tab__Header__Button"
              disabled={isLoadingExportBookingList}
              onClick={onExportBookingList}
            >
              <img src={DownloadIcon} alt="download" />
              <span>Xuất file</span>
              <img src={DropDownIcon} alt="dropdown" />
            </Button>
          </div>

          <TabContent
            activeTab={searchQueries.statusType || BOOKING_TAB.ALL}
            className="Table__Body"
          >
            <Label className="Table__Body__Label">
              {`${bookingTotal || 0} đơn đặt phòng`}
            </Label>

            {NAV_TAB.map((item) => (
              <TabPane tabId={item.value} key={item.value}>
                <DataTableBooking
                  bookings={bookingList}
                  isIntranet={false}
                />
              </TabPane>
            ))}
          </TabContent>
        </Card>

        {bookingTotal !== 0 && (
          <Pagination
            page={+searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </Container>
    </div>
  );
}

LocationManagerBooking.propTypes = {
  loadingBookingList: PropTypes.bool.isRequired,
  isLoadingExportBookingList: PropTypes.bool.isRequired,

  bookingTotal: PropTypes.number,
  bookingList: PropTypes.instanceOf(Array),

  actionGetMyPlace: PropTypes.func.isRequired,
  actionGetBookingList: PropTypes.func.isRequired,
  actionExportBookingList: PropTypes.func.isRequired,
};

LocationManagerBooking.defaultProps = {
  bookingTotal: 0,
  bookingList: [],
};

export default LocationManagerBooking;
